import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { UserPlus, Loader2, CheckCircle2, Link2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountantLinkClient = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/accountant/redeem-code`, { code: code.trim() });
      setResult(res.data);
      toast.success('Code redeemed! Waiting for client confirmation.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto space-y-6" data-testid="link-client-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Link a Client</h1>
        <p className="text-slate-400 text-sm mt-1">Enter the link code provided by your seafarer client</p>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Link2 className="h-5 w-5 text-sky-400" />
            Enter Client Link Code
          </CardTitle>
        </CardHeader>
        <CardContent>
          {result ? (
            <div className="text-center py-4">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-white font-medium mb-1">Code Redeemed Successfully</p>
              <p className="text-slate-400 text-sm">
                Waiting for <span className="text-white">{result.client_name}</span> to confirm and grant access.
              </p>
              <Button onClick={() => { setResult(null); setCode(''); }} variant="outline"
                className="mt-4 border-slate-600 text-slate-300" data-testid="link-another-btn">
                Link Another Client
              </Button>
            </div>
          ) : (
            <form onSubmit={handleRedeem} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Link Code</Label>
                <Input
                  placeholder="SED-XXXX-XXXX"
                  value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="bg-slate-900/80 border-slate-600 text-white placeholder:text-slate-500 h-11 text-center text-lg tracking-wider font-mono"
                  required data-testid="link-code-input"
                />
                <p className="text-xs text-slate-500">
                  Ask your client to generate this code from their Seafarer Logbook app under Settings.
                </p>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-sky-600 hover:bg-sky-700 h-10" data-testid="redeem-code-btn">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</> : <><UserPlus className="mr-2 h-4 w-4" />Redeem Code</>}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <p className="text-xs text-slate-500 font-medium mb-2">How client linking works:</p>
          <ol className="text-xs text-slate-400 space-y-1.5 list-decimal list-inside">
            <li>Your client generates a link code from their Seafarer Logbook app</li>
            <li>You enter the code above to request access</li>
            <li>Your client reviews and confirms access with GDPR consent</li>
            <li>You gain read-only access to their SED data, trips, and documents</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountantLinkClient;
